import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import type { WebFrequency } from '@/mocks/superAdminFrequencies';
import { frequencyPreferredConfig } from '@/mocks/superAdminFrequencies';

interface NuevaSolicitudModalProps {
  frequencies: WebFrequency[];
  onClose: () => void;
  onCreate: (data: { radioName: string; owner: string; requestedFrequency: string; numericValue: number; currentFrequency: string; plan: string; country: string; notes: string }) => void;
}

const MIN_FREQ = 87.00;
const MAX_FREQ = 200.00;
const STEP = 0.01;

function roundToStep(val: number): number {
  return Math.round(val / STEP) * STEP;
}

function findFrequency(freqs: WebFrequency[], numeric: number): WebFrequency | undefined {
  return freqs.find((f) => Math.abs(f.numericValue - numeric) < 0.001);
}

function formatFreq(n: number): string {
  return `${n.toFixed(2)} RFW`;
}

function calculatePrice(plan: string, country: string) {
  const config = frequencyPreferredConfig;
  const planPrice = config.plansPricing.find((p) => p.plan === plan);
  const countryPrice = config.countriesPricing.find((c) => c.country === country);
  const base = planPrice ? config.basePrice + planPrice.price : config.basePrice;
  const countryExtra = countryPrice ? countryPrice.additionalCharge : 0;
  const planExtra = planPrice ? planPrice.additionalCharge : 0;
  const subtotal = base + countryExtra + planExtra;

  const now = new Date().toISOString().split('T')[0];
  const isPromo =
    config.promotionalPrice !== null &&
    config.promotionalStartDate !== null &&
    config.promotionalEndDate !== null &&
    now >= config.promotionalStartDate &&
    now <= config.promotionalEndDate;

  const priceBeforeTax = isPromo ? config.promotionalPrice : subtotal;
  const tax = Math.round(priceBeforeTax * (config.taxRate / 100) * 100) / 100;
  const total = Math.round((priceBeforeTax + tax) * 100) / 100;

  return { subtotal, tax, total, isPromo, currency: config.currency };
}

const presetRadios = [
  { name: 'Radio Ritmo Latino', owner: 'Miguel Ángel Ríos', freq: '88.35 RFW', plan: 'Pro', country: 'México' },
  { name: 'Radio Éxitos TV', owner: 'Carlos Méndez', freq: '100.00 RFW', plan: 'Empresarial', country: 'México' },
  { name: 'Rock Total Radio', owner: 'Pedro Ramírez', freq: '95.70 RFW', plan: 'Business', country: 'Argentina' },
  { name: 'Radio Sonora Digital', owner: 'Ana López', freq: '105.22 RFW', plan: 'Pro', country: 'México' },
  { name: 'Radio Fiesta Mix', owner: 'Luis Torres', freq: '89.90 RFW', plan: 'Premium', country: 'España' },
  { name: 'La Voz del Llano', owner: 'María Fernández', freq: '92.15 RFW', plan: 'Premium', country: 'Colombia' },
  { name: 'Radio Urbana 101', owner: 'Javier Herrera', freq: '91.45 RFW', plan: 'Business', country: 'Colombia' },
  { name: 'Radio Internacional Voz', owner: 'Diana Castro', freq: '96.80 RFW', plan: 'Pro', country: 'Chile' },
  { name: 'Deportes Total Radio', owner: 'Roberto Sánchez', freq: '120.00 RFW', plan: 'Business', country: 'Argentina' },
  { name: 'Radio Élite Digital', owner: 'Sofía Vargas', freq: '91.30 RFW', plan: 'Pro', country: 'Perú' },
];

const presetPlans = ['Free', 'Básico', 'Pro', 'Premium', 'Business', 'Empresarial', 'Oro', 'Regional', 'Nacional', 'Ilimitado'];
const presetCountries = ['México', 'Colombia', 'Argentina', 'Chile', 'Perú', 'España', 'Estados Unidos'];

export default function NuevaSolicitudModal({ frequencies, onClose, onCreate }: NuevaSolicitudModalProps) {
  const [radioName, setRadioName] = useState('');
  const [owner, setOwner] = useState('');
  const [currentFrequency, setCurrentFrequency] = useState('');
  const [plan, setPlan] = useState('Pro');
  const [country, setCountry] = useState('México');
  const [notes, setNotes] = useState('');
  const [freqInput, setFreqInput] = useState('');
  const [numericValue, setNumericValue] = useState<number | null>(null);
  const [validation, setValidation] = useState<{ valid: boolean; status: string; message: string } | null>(null);
  const [step, setStep] = useState<1 | 2>(1);

  const pricePreview = plan && country ? calculatePrice(plan, country) : null;

  useEffect(() => {
    if (numericValue !== null && numericValue >= MIN_FREQ && numericValue <= MAX_FREQ) {
      const found = findFrequency(frequencies, numericValue);
      if (!found) {
        setValidation({ valid: false, status: 'error', message: 'Frecuencia fuera del rango WEB (87.00–200.00).' });
      } else if (found.status === 'libre') {
        setValidation({ valid: true, status: 'libre', message: `¡${formatFreq(numericValue)} está disponible!` });
      } else if (found.status === 'asignada') {
        setValidation({ valid: false, status: 'asignada', message: `${formatFreq(numericValue)} ya está asignada a ${found.assignedRadio || 'una radio'}.` });
      } else if (found.status === 'reservada') {
        setValidation({ valid: false, status: 'reservada', message: `${formatFreq(numericValue)} está reservada para ${found.reservedFor || 'otra radio'}.` });
      } else if (found.status === 'bloqueada') {
        setValidation({ valid: false, status: 'bloqueada', message: `${formatFreq(numericValue)} está bloqueada: ${found.blockedReason || 'motivo administrativo'}.` });
      }
    } else if (numericValue !== null) {
      setValidation({ valid: false, status: 'error', message: `Fuera del rango 87.00–200.00 RFW.` });
    } else {
      setValidation(null);
    }
  }, [numericValue, frequencies]);

  const handleFreqInput = (val: string) => {
    setFreqInput(val);
    const parsed = parseFloat(val);
    if (!isNaN(parsed) && parsed >= MIN_FREQ && parsed <= MAX_FREQ) {
      const rounded = roundToStep(parsed);
      setNumericValue(rounded);
      setFreqInput(rounded.toFixed(2));
    } else if (val === '' || val === '.') {
      setNumericValue(null);
    } else if (!isNaN(parsed)) {
      setNumericValue(parsed);
    } else {
      setNumericValue(null);
    }
  };

  const selectPreset = (idx: number) => {
    const p = presetRadios[idx];
    setRadioName(p.name);
    setOwner(p.owner);
    setCurrentFrequency(p.freq);
    setPlan(p.plan);
    setCountry(p.country);
  };

  const handleNext = () => {
    if (!radioName.trim()) {
      Swal.fire({ icon: 'warning', title: 'Nombre requerido', text: 'Ingresá el nombre de la radio.', confirmButtonColor: '#1a1a1a', customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' } });
      return;
    }
    if (!owner.trim()) {
      Swal.fire({ icon: 'warning', title: 'Propietario requerido', text: 'Ingresá el nombre del propietario.', confirmButtonColor: '#1a1a1a', customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' } });
      return;
    }
    setStep(2);
  };

  const handleCreate = () => {
    if (!numericValue || !validation?.valid || !radioName.trim()) return;
    if (!owner.trim()) {
      Swal.fire({ icon: 'warning', title: 'Propietario requerido', text: 'Completá el paso anterior.', confirmButtonColor: '#1a1a1a', customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' } });
      setStep(1);
      return;
    }

    Swal.fire({
      icon: 'question',
      title: '¿Crear solicitud?',
      html: `
        <div style="text-align:left;font-size:12px;">
          <p><strong>Radio:</strong> ${radioName}</p>
          <p><strong>Propietario:</strong> ${owner}</p>
          <p><strong>Frecuencia:</strong> <span style="font-family:monospace;">${formatFreq(numericValue)}</span></p>
          <p><strong>País:</strong> ${country}</p>
          <p style="margin-top:8px;"><strong>Precio calculado:</strong></p>
          <p style="font-family:monospace;">$${pricePreview?.total.toFixed(2) || '0.00'} ${pricePreview?.currency || 'USD'}${pricePreview?.isPromo ? ' 🏷️ Promo' : ''}</p>
          <p style="color:#6b7280;font-size:10px;">Subtotal: $${pricePreview?.subtotal.toFixed(2) || '0.00'} + Tax (${frequencyPreferredConfig.taxRate}%): $${pricePreview?.tax.toFixed(2) || '0.00'}</p>
          <p style="color:#059669;margin-top:4px;">La frecuencia está disponible.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Crear solicitud',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a1a1a',
      cancelButtonColor: '#d1d5db',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
    }).then((r) => {
      if (r.isConfirmed) {
        onCreate({
          radioName: radioName.trim(),
          owner: owner.trim(),
          requestedFrequency: formatFreq(numericValue),
          numericValue,
          currentFrequency: currentFrequency || 'No especificada',
          plan,
          country,
          notes: notes.trim() || '',
        });
        Swal.fire({ icon: 'success', title: 'Solicitud creada', text: `Pendiente de pago: $${pricePreview?.total.toFixed(2) || '0.00'} ${pricePreview?.currency || 'USD'}`, toast: true, position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: true, customClass: { popup: 'rounded-2xl text-sm', title: 'text-xs font-semibold' } });
        onClose();
      }
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-[80] bg-black/40" onClick={onClose}></div>
      <div className="fixed inset-0 z-[81] flex items-center justify-center p-4">
        <div className="bg-background-50 rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-background-200">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                <i className="ri-vip-diamond-line text-accent-600 text-sm"></i>
              </div>
              <div>
                <h3 className="text-foreground-800 text-sm font-bold">
                  {step === 1 ? 'Nueva Solicitud — Datos' : 'Nueva Solicitud — Frecuencia'}
                </h3>
                <p className="text-foreground-400 text-[10px]">Preferred Frequency RFW</p>
              </div>
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>

          {step === 1 && (
            <div className="p-4 space-y-4">
              <p className="text-foreground-400 text-[10px]">Seleccioná una radio predefinida o ingresá los datos manualmente.</p>

              <div className="flex flex-wrap gap-1.5">
                {presetRadios.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => selectPreset(i)}
                    className={`px-2.5 py-1.5 rounded-full text-[9px] font-medium transition-all cursor-pointer whitespace-nowrap ${radioName === r.name ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700'}`}
                  >
                    {r.name}
                  </button>
                ))}
              </div>

              <div className="border-t border-background-200 pt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">Nombre de la Radio</span>
                    <input type="text" value={radioName} onChange={(e) => setRadioName(e.target.value)} placeholder="Ej: Radio Éxitos TV" className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300" />
                  </label>
                  <label className="block">
                    <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">Propietario</span>
                    <input type="text" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Ej: Carlos Méndez" className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300" />
                  </label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <label className="block">
                    <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">Frecuencia Actual</span>
                    <input type="text" value={currentFrequency} onChange={(e) => setCurrentFrequency(e.target.value)} placeholder="Ej: 89.90" className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300" />
                  </label>
                  <label className="block">
                    <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">Plan</span>
                    <select value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 cursor-pointer">
                      {presetPlans.map((p) => (<option key={p} value={p}>{p}</option>))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">País</span>
                    <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 cursor-pointer">
                      {presetCountries.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
                <button onClick={handleNext} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                  Siguiente <i className="ri-arrow-right-line ml-1 text-[11px]"></i>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="p-4 space-y-4">
              <div className="bg-background-100 rounded-xl p-3 space-y-1.5">
                <div className="flex items-center gap-2">
                  <i className="ri-radio-line text-foreground-500 text-sm"></i>
                  <span className="text-foreground-800 text-xs font-semibold">{radioName}</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-400 text-[10px]">
                  <span>{owner}</span>
                  <span>·</span>
                  <span>{plan}</span>
                  <span>·</span>
                  <span>{country}</span>
                </div>
                {pricePreview && (
                  <div className="bg-white rounded-lg p-2 flex items-center justify-between">
                    <span className="text-foreground-400 text-[9px]">Precio estimado:</span>
                    <span className="text-foreground-800 text-xs font-bold font-mono">
                      ${pricePreview.total.toFixed(2)} {pricePreview.currency}
                      {pricePreview.isPromo && <span className="ml-1 text-accent-600 text-[9px]">🏷️ Promo</span>}
                    </span>
                  </div>
                )}
              </div>

              <label className="block">
                <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">Frecuencia Solicitada (87.00 – 200.00 RFW)</span>
                <div className="relative mt-1">
                  <input type="number" step="0.01" min={MIN_FREQ} max={MAX_FREQ} value={freqInput} onChange={(e) => handleFreqInput(e.target.value)} placeholder="Ej: 94.70" className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm font-mono font-bold outline-none focus:border-foreground-300" />
                  {numericValue !== null && (<span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs font-mono">RFW</span>)}
                </div>
              </label>

              {validation && (
                <div className={`rounded-xl p-3 flex items-start gap-2.5 ${validation.valid ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${validation.valid ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <i className={`${validation.valid ? 'ri-check-line text-emerald-600' : 'ri-close-line text-red-500'} text-sm`}></i>
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${validation.valid ? 'text-emerald-700' : 'text-red-600'}`}>{validation.valid ? 'Disponible' : 'No disponible'}</p>
                    <p className={`text-[10px] leading-relaxed ${validation.valid ? 'text-emerald-600/80' : 'text-red-500/80'}`}>{validation.message}</p>
                  </div>
                </div>
              )}

              <label className="block">
                <span className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider">Notas (opcional)</span>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Motivo del cambio de frecuencia..." maxLength={200} rows={2} className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 resize-y"></textarea>
              </label>

              <div className="flex justify-between gap-2 pt-2">
                <button onClick={() => setStep(1)} className="px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-left-line mr-1 text-[11px]"></i>Volver
                </button>
                <button onClick={handleCreate} disabled={!validation?.valid} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed">
                  <i className="ri-add-line mr-1 text-[11px]"></i>Crear Solicitud
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}